/**
 * Cash Book Screen - Using Report Engine
 * Standardized layout with database-driven columns, export, and column settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Calendar, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ReportLayout } from '@/components/report-engine';
import api from '@/services/api';

const CashBookScreen = ({ theme }) => {
  const isDark = theme === 'dark';
  
  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Summary state
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  useEffect(() => {
    fetchCashBook();
  }, [fromDate, toDate, page, pageSize]);

  const fetchCashBook = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use CASH_BOOK report code with server-side pagination
      const response = await api.post('/api/report-engine/data/CASH_BOOK', {
        page,
        pageSize,
        filters: { fromDate, toDate, search: searchQuery || undefined }
      });

      if (response.data.success) {
        const rows = response.data.data?.rows || [];
        const paginationData = response.data.data;
        
        // Map data to expected format
        const processedData = rows.map(r => ({
          ...r,
          DocDate: r.DocDateFormatted || r.DocDate,
          AccountName: r.AccountName,
          Narration: r.CommNarration,
          Receipt: parseFloat(r.DrAmount) || 0,
          Payment: 0,
          Balance: 0
        }));

        // Calculate running balance
        let runningBalance = openingBalance;
        processedData.forEach(row => {
          runningBalance += row.Receipt - row.Payment;
          row.Balance = runningBalance;
        });

        setData(processedData);
        setTotalRows(paginationData.total || 0);
        setTotalPages(paginationData.totalPages || 0);

        // Calculate totals from current page
        const totReceipts = processedData.reduce((sum, r) => sum + r.Receipt, 0);
        const totPayments = processedData.reduce((sum, r) => sum + r.Payment, 0);
        setTotalReceipts(totReceipts);
        setTotalPayments(totPayments);
        setClosingBalance(openingBalance + totReceipts - totPayments);
      }
    } catch (err) {
      console.error('Error fetching cash book:', err);
      setError('Failed to load cash book data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    fetchCashBook();
  }, [fromDate, toDate, page, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Filter data by search query
  const filteredData = data.filter(row =>
    !searchQuery ||
    row.DocId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.AccountName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.CommNarration?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Summary cards
  const summaryCards = [
    { 
      label: 'Opening Balance', 
      value: formatCurrency(openingBalance),
      icon: <Wallet size={16} />
    },
    { 
      label: 'Total Receipts', 
      value: formatCurrency(totalReceipts), 
      variant: 'success',
      icon: <ArrowUpRight size={16} />
    },
    { 
      label: 'Total Payments', 
      value: formatCurrency(totalPayments), 
      variant: 'danger',
      icon: <ArrowDownRight size={16} />
    },
    { 
      label: 'Closing Balance', 
      value: formatCurrency(closingBalance),
      variant: closingBalance >= 0 ? 'success' : 'danger',
      icon: <Wallet size={16} />
    }
  ];

  // Toolbar filters
  const FiltersToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Calendar size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="cashbook-from-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="cashbook-to-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
      </div>
      
      {/* Search */}
      <input
        type="text"
        placeholder="Search voucher, account..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="cashbook-search"
        className={`px-3 py-1.5 rounded-lg border text-sm w-48 ${
          isDark ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
        }`}
      />
    </div>
  );

  return (
    <ReportLayout
      reportCode="CASH_BOOK"
      reportTitle="Cash Book"
      reportSubtitle={`${new Date(fromDate).toLocaleDateString('en-IN')} to ${new Date(toDate).toLocaleDateString('en-IN')}`}
      reportIcon={<Wallet size={20} />}
      data={filteredData}
      loading={loading}
      error={error}
      onRefresh={handleRefresh}
      theme={theme}
      summaryCards={summaryCards}
      filters={FiltersToolbar}
      showRowNumbers={true}
      pagination={{ page, pageSize, total: totalRows, totalPages }}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
};

export default CashBookScreen;
